import dayjs from 'dayjs';
import type { Reactive } from 'vue';
import type { LoginInfo, UserInfo } from '~/api/interface';
import { SESSION_SOURCE } from '~/constants/config';
import type { Area } from './area';

/**
 * 埋点规范：
 * 1. key，英文大写 + 下划线。点击事件CLICK_XXXX。逻辑执行后跳转ROUTE_XXXX。弹窗拉起POPUP_XXXX。页面展示PAGE_XXXX
 * 2. value, 英文小写 + 下划线。服务平台事件统一youyan_platform_xxxx
 * 3. 所有埋点添加注释
 */

export enum TRACK_EVENT {
  CLICK_SEARCH_WORK = 'youyan_platform_search_work', // 点击搜索作品
  CLICK_HOME_TUTORIAL_PAGE_SHOW = 'youyan_platform_home_tutorial_page_show', // 点击首页1分钟视频卡片
  CLICK_UPDATE_LOG_MORE = 'youyan_platform_update_log_more', // 点击“查看完整迭代日志”按钮
  CLICK_UPDATE_LOG = 'youyan_platform_update_log', // 播放迭代更新视频
  CLICK_UPDATE_LOG_PLAY = 'youyan_platform_update_log_play', // 播放迭代更新视频
  CLICK_CHARACTER = 'youyan_platform_3davatar', // 点击导航“3D数字人”
  CLICK_SCHOOL = 'youyan_platform_school',
  CLICK_PAGE_NEWS = 'youyan_platform_page_news', // 点击新闻页面

  PAGE_BRAND_DETAIL = 'youyan_official_brand_detail', // 查看客户案例详情页面
  CLICK_BRAND_PLAY_VIDEO = 'youyan_official_brand_play_video', // 播放客户案例视频
  CLICK_BRAND_FREE_TO_USE = 'youyan_official_brand_free_to_use', // 客户案例免费试用

  PAGE_NEWS = 'youyan_official_news', // 新闻页面
  CLICK_HOME = 'youyan_official_homepage', // 点击首页
  CLICK_TEMPLATE = 'youyan_official_template', // 点击模板中心
  CLICK_SCENARIO = 'youyan_official_scenario', // 点击应用场景
  CLICK_PAYPAGE = 'youyan_official_paypage', // 点击付费订阅
  CLICK_SUPPORT = 'youyan_official_support', // 帮助支持

  // 移动端
  MOBILE_HOMEPAGE = 'youyan_mobile_homepage', // 进入移动端首页
  MOBILE_GUIDE_TO_OFFICIAL_WEB_1 = 'youyan_mobile_GuidetoOfficial_web1', // 点击引导至官网1
  MOBILE_GUIDE_TO_OFFICIAL_WEB_2 = 'youyan_mobile_GuidetoOfficial_web2', // 点击引导至官网2
  MOBOLE_PLAY_MAIN_VIDEO = 'youyan_mobile_PalyMainVideo', // 点击播放主视频

  PROFILE_PHOTO = 'youyan_official_ProfilePhoto', // 点击头像进入服务平台首页
  CLICK_LOGIN = 'youyan_official_login', // 点击登录
  CLICK_SCENARIO_VIDEO = 'youyan_official_ClickScenario', // 点击查看应用场景视频
  CLICK_START_CREATE_1 = 'youyan_official_StartCreate1', // 点击首屏的 开始创作
  CLICK_START_CREATE_2 = 'youyan_official_StartCreate2', // 点击第二屏 开始创作
  CLICK_START_CREATE_3 = 'youyan_official_StartCreate3', // 点击第三屏 开始创作
  START_CREATE_4 = 'youyan_official_StartCreate4', // 点击第四屏 开始创作
  START_CREATE_5 = 'youyan_official_StartCreate5', // 点击第五屏 开始创作
  START_CREATE_6 = 'youyan_official_StartCreate6', // 点击第六屏 开始创作
  CLICK_FUNCTIONAL_BTN = 'youyan_official_functional_intro', // 功能介绍切换点击
  CLICK_BRAND_CARD = 'youyan_official_brand_card', // 客户案例卡片点击
  CLICK_BRAND_DETAIL_OTHER_CARD = 'youyan_official_brand_detail_other_card', // 客户案例详情，点击其他案例
  CLICK_START_CREATE_7 = 'youyan_official_StartCreate7', // 点击尾屏 开启免费体验
  // LOGO = 'youyan_official_logo', // 点击尾屏 有言logo
  CLICK_LEGAL_NOTICE = 'youyan_official_LegalNotice', // 点击 法律声明
  CLICK_PRIVACY_POLICY = 'youyan_official_PrivacyPolicy', // 点击 隐私政策
  CLICK_USER_POLICY = 'youyan_official_UserPolicy', // 点击 用户协议
  START_CREATE_8 = 'youyan_official_StartCreate8', // 点击 制作同款视频
  PAGE_LANDING_PAGE = 'youyan_official_landingpage', // landingpage查看

  // 编辑器要求埋点
  BOOT_EDITOR = 'yy_editor_boot',

  // 页面访问性能埋点
  VITALS_CLS = 'web_vitals_CLS',
  VITALS_FCP = 'web_vitals_FCP',
  VITALS_LCP = 'web_vitals_LCP',
  VITALS_TTFB = 'web_vitals_TTFB',
  VITALS_INP = 'web_vitals_INP',

  // oss 资源访问埋点
  IMAGE_BENCHMARK = 'benchmark_image', // 图片资源加载性能
  VIDEO_BENCHMARK = 'benchmark_video', // 视频资源加载性能

  CLICK_PRODUCT_HIGHLIGHTS_CARD = 'youyan_platform_ProductHighlights', // 点就亮点视频卡片
  PLAY_HIGHLIGHTS_VIDEO = 'youyan_platform_ProductHighlights_video', // 播放亮点视频
  CLICK_PLAY_HIGHLIGHTS_VIDEO = 'youyan_platform_highlights_video', // 播放亮点视频
  CLICK_HIGHLIGHTS_EXPERIENCE_NOW = 'youyan_platform_ProductHighlights_experience', // 亮点视频点击立即体验

  PAGE_INDUSTRY_APP_LIST = 'youyan_platform_IndustryApplications',
  CLICK_INDUSTRY_APP_CARD = 'youyan_platform_ClickIndustryApplications_DetailsPage', // 点击行业应用卡片
  CLICK_INDUSTRY_EXPERIENCE_NOW = 'youyan_platform_industry_application_experience_now', // 点击行业集合下方立即体验
  PAGE_INDUSTRY_DETAIL = 'youyan_platform_IndustryApplications_DetailsPage', // 查看行业详情页面
  PLAY_INDUSTRY_VIDEO = 'youyan_platform_industry_application_video', // 播放行业应用视频
  CLICK_INDUSTRY_DETAIL_CREATE = 'youyan_platform_industry_application_create', // 点击开始创作
  CLICK_INDUSTRY_OTHER_CARD = 'youyan_platform_industry_other_card', // 点击其他行业应用视频卡片
  CLICK_INDUSTRY_VIEW_MORE = 'youyan_platform_industry_view_more', // 点击行业应用“查看更多”
  CLICK_INDUSTRY_DETAIL_CONTACT_US = 'youyan_platform_industry_detail_contact_us', // 点击行业应用底下“联系我们”

  // '浏览企业案例集合页面' = 'youyan_platform_EnterpriseCases',
  CLICK_HOME_ENTERPRISE_CASE_CARD = 'youyan_platform_home_enterprise_cases_card', // 点击首页客户案例卡片
  CLICK_CASE_DETAIL = 'youyan_platform_ClickEnterpriseCases_DetailsPage', // 点击列表页面客户案例卡片

  PAGE_ENTERPRISE_CASE_LIST = 'youyan_platform_enterprise_cases_list', // 查看客户案例列表
  CLICK_ENTERPRISE_CASE_CONTACT_US = 'youyan_platform_enterprise_case_contact_us', // 企业案例集合页底部“联系我们”
  PAGE_ENTERPRISE_CASE_DETAIL = 'youyan_platform_enterprise_cases_detail', // 查看客户案例详情

  PAGE_USER_EXCELLENT_WORK_LIST = 'youyan_platform_UserExcellentWorks', // 个人作品集合页面
  // CLICK_USER_EXCELLENT_WORK_CARD = 'youyan_platform_UserExcellentWorks_DetailsPage', // 点击个人作品集合页面卡片
  CLICK_USER_EXCELLENT_WORK_SWITCH = 'youyan_platform_excellent_work_switch', // 个人作品集合页面切换横竖屏筛选

  CLICK_EXCELLENT_WORK = 'youyan_platform_excellent_work', // 点击个人作品

  PREVIEW_EXCELLENT_WORK = 'youyan_platform_preview_excellent_work', // 预览个人作品

  CLICK_POPUP_TEMPLATE_TAG = 'youyan_platform_template_tag', // 点击tag

  CLICK_CHARACTER_TAG = 'youyan_platform_3davatar_tag', // 点击数字人标签

  CLICK_TEMPLATE_FORMAT_FILTER = 'youyan_platform_template_format_filter', // 点击模板横竖屏filter筛选
  CLICK_TEMPLATE_CENTER_TAG_FILTER = 'youyan_platform_template_center_tag_filter', // 点击模板页面tag筛选
  CLICK_TEMPLATE_CENTER_STUDIO_FILTER = 'youyan_platform_template_center_studio_center', // 模板中心演播室筛选
  CLICK_TEMPLATE_CENTER_SORT_FILTER = 'youyan_platform_template_center_sort_filter', // 模板中心排序

  // 归档埋点
  CLICK_LOGO = 'youyan_platform_logo', // logo点击
  CLICK_HOME_TOP = 'youyan_platform_HomepageTop', // 首页按钮点击-顶部
  CLICK_TEMPLATE_CENTER = 'youyan_platform_template_center', // 首页按钮点击-作品中心
  CLICK_SCENARIOS = 'youyan_platform_scenarios', // 点击应用场景
  CLICK_SUBSCRIBE = 'youyan_platform_subscribe', // 点击付费订阅
  CLICK_TUTORIAL = 'youyan_platform_tutorial', // 点击教程
  CLICK_HELP_CENTER = 'youyan_platform_help_center', // 点击帮助中心
  CLICK_GOTO_LOGIN = 'youyan_platform_goto_login', // 点击注册登录

  CLICK_HOME_LEFT = 'youyan_platform_HomepageLeft_nav', // 首页按钮点击-左侧
  CLICK_RECENTLY_WORK = 'youyan_platform_recently_work_nav', // 最近作品
  CLICK_COLLECT = 'youyan_platform_collect_nav', // 我的收藏
  CLICK_PERSONAL_SPACE = 'youyan_platform_personal_space_nav', // 个人空间
  CLICK_TEAM_SPACE = 'youyan_platform_team_space_nav', // 团队空间
  CLICK_BRAND_KIT = 'youyan_platform_brand_kit_nav', // 品牌规范

  CLICK_SUSPENDED_CONTACT_US = 'youyan_platform_suspended_contact_us', // 点击悬浮 - 联系我们
  CLICK_SUSPENDED_CONTACT_CUSTOMER_SERVICE = 'youyan_platform_suspended_contact_customer_service', // 点击悬浮 - 联系客服
  CLICK_SUSPENDED_HELP_CENTER = 'youyan_platform_suspended_help_center', // 点击悬浮 - 帮助中心
  CLICK_SUSPENDED_FEEDBACK = 'youyan_platform_suspended_feedback', // 点击悬浮 - 意见反馈
  CLICK_SUSPENDED_WECHAT = 'youyan_platform_suspended_wechat', // 点击悬浮-微信咨询
  CLICK_SUSPENDED_PHONE = 'youyan_platform_suspended_phone', // 点击悬浮-电话咨询
  CLICK_SUSPENDED_YANSHI = 'youyan_platform_suspended_yanshi', // 点击悬浮-预约演示
  CLICK_SUSPENDED_CASE = 'youyan_platform_suspended_case', // 点击悬浮-行业案例
  CLICK_SUSPENDED_AI_CAMP = 'youyan_platform_suspended_ai_camp', // 点击悬浮-ai训练营

  PAGE_HOME = 'youyan_platform_homepage', // 首页页面展示
  CLICK_HOME_BANNER = 'youyan_platform_home_banner', // 点击首页banner

  PAGE_TEMPLATE = 'youyan_platform_template_page_show', // 模板中心页面展示
  PAGE_TEMPLATE_DETAIL = 'youyan_platform_template_page_detail_show', // 模板详情页面展示

  CLICK_CONTACT_US = 'youyan_platform_contact_us', // 点击联系我们

  CLICK_TEMPLATE_PREVIEW = 'youyan_platform_template_click', // 点击预览模板
  PLAY_TEMPLATE_VIDEO = 'youyan_platform_play_template_video', // 播放平台模板视频

  PAGE_SCENARIOS = 'youyan_platform_scenarios_page_show', // 应用场景页面展示

  PAGE_SUBSCRIBE = 'youyan_platform_pay_page_show', // 付费订阅页面展示
  CLICK_SUBSCRIBE_PURCHASE = 'youyan_platform_subscribe_purchase', // 付费订阅页面的套餐订购/续订/咨询
  CLICK_SUBSCRIBE_PACKAGE_SWITCH = 'youyan_platform_subscribe_package_switch', // 付费订阅套餐switch切换

  PAGE_PERSONAL_PAGE = 'youyan_platform_personal_page_show', // 个人主页

  POPUP_PAY_PAGE_CLICK = 'POPUP_PAY_PAGE_CLICK', // 付费订阅弹窗点击
  YOUYAN_PLATFORM_PAYTOKEN_PAGE_CLICK = 'youyan_platform_paytoken_page_click', // 加购言币弹窗点击  todo: 暂时没有用到
  PAGE_TUTORIAL = 'youyan_platform_tutorial_page_show', // 教程页面展示
  ROUTE_HELP = 'youyan_platform_help_page_show', // 帮助中心页面展示
  ROUTE_HELP_DETAIL = 'youyan_platform_HelpCenter_page_show', // 帮助中心详情页展示
  ROUTE_CHANGE_LOG = 'youyan_platform_change_log_page_show', // 更新日志页面展示

  MESSAGE_CENTER_CLICK = 'youyan_platform_notify_click', // 消息通知弹窗点击
  YOUYAN_PLATFORM_CREATE_NEW_CLICK = 'youyan_platform_CreateNew_click', // 新建作品点击
  ROUTE_ACCOUNT_MANGE = 'youyan_platform_account_page_show', // 账号设置页面展示
  ROUTE_CONSUME = 'youyan_platform_PayDetail_page_show', // 消耗记录页面展示
  PAGE_TEAM_MANEGE = 'youyan_platform_TeamManagement_page_show', // 团队管理页面展示
  PAGE_EQUITY_MANAGE = 'youyan_platform_RightManagement_page_show', // 权益管理页面展示
  PAGE_ORDER_MANAGE = 'youyan_platform_invoice_page_show', // 订单发票页面展示
  SUBMIT_ORDER_APPLY = 'youyan_platform_submit_order_apply', // 提交订单申请
  TOGGLE_ACCOUNT_TOP_CLICK = 'youyan_platform_ChangeAccount_ClickTop', // 切换账号点击（右上角
  YOUYAN_PLATFORM_HOMEPAGE_TOP = 'youyan_platform_Homepage_TopCard', // 首页置顶卡片点击
  // CLICK_TO_LOGIN = 'youyan_platform_login_click', // 点击后触发登录
  PAGE_LOGIN = 'youyan_platform_login_page_show', // 登录页
  ROUTE_ACCOUNT_VERIFY = 'youyan_platform_account_verify_page_show',
  ROUTE_ACCOUNT_RESULT = 'youyan_platform_account_result_page_show',
  PAGE_RECENTLY = 'youyan_platform_recently_page_show',
  PAGE_COLLECTION = 'youyan_platform_collect_page_show',
  PAGE_PERSONAL_SPACE = 'youyan_platform_MySpace_page_show',
  PAGE_TEAM_SPACE = 'youyan_platform_TeamSpace_page_show',
  PAGE_BRAND = 'youyan_platform_Brand_page_show',
  PAGE_SHARE_WITH_ME = 'youyan_platform_ShareWithMe_page_show',
  ROUTE_INVITE = 'youyan_platform_activity_invitingnew_show', // 拉新页面
  ROUTE_PRODUCT_GUIDE = 'route_product_guide', // 产品指南
  ROUTE_ACCREDIT = 'route_accredit', // 授权书签署页面
  YOUYAN_PLATFORM_INVITE_LOGIN = 'youyan_platform_activity_invitingnew_loginclick', // 拉新页面登录按钮
  YOUYAN_PLATFORM_INVITE_LINK = 'youyan_platform_activity_invitingnew_get_link', // 获取拉新链接
  YOUYAN_PLATFORM_INVITE_LINK_COPY = 'youyan_platform_activity_invitingnew_copy_link', // 拉新复制邀请链接
  YOUYAN_PLATFORM_INVITE_RANK = 'youyan_platform_activity_invitingnew_charts_show', // 邀请排行榜点击
  CLICK_TEMPLATE_USE = 'youyan_platform_template_use', // 使用模板
  CLICK_LOGIN_BTN = 'youyan_platform_login_button_click', // 登录按钮点击
  CLICK_LOGIN_FORGET_BTN = 'youyan_platform_login_forget_click', // 点击忘记密码
  PASSWORD_CHANGE_SUCCESS = 'youyan_platform_change_password_success', // 密码修改成功

  LOGIN_GOOGLE_CLICK = 'youyan_platform_Googlelogin', // 第三方登录点击
  GOOGLE_LOGIN_SUCCESS = 'youyan_platform_Googlelogin_success', // 第三方登录成功
  HEADER_CREATE = 'youyan_platform_platform_CreateNew_click',
  TOGGLE_ACCOUNT_BOTTOM_CLICK = 'youyan_platform_ChangeAccount_ClickLeft',
  LOGIN_OUT_CLICK = 'youyan_platform_QuitAccount_click',
  YOUYAN_PLATFORM_SHARE = 'youyan_platform_share', // 分享
  YOUYAN_PLATFORM_SHARE_CHARACTER = 'youyan_platform_share_3davatar', // 分享3d数字人
  YOUYAN_PLATFORM_COLLECT = 'youyan_platform_collect', // 收藏
  YOUYAN_PLATFORM_CANCEL_COLLECT = 'youyan_platform_CancelCollect', // 取消收藏
  YOUYAN_PLATFORM_COLLECT_CHARACTER = 'youyan_platform_collect_3davatar', // 收藏数字人
  YOUYAN_PLATFORM_CANCEL_COLLECT_CHARACTER = 'youyan_platform_CancelCollect_3davatar', // 取消搜藏数字人
  OPEN_OPUS = 'youyan_platform_OpenProject', // 打开作品
  SAVE_AS_NEW_OPUS_AND_EDIT = 'youyan_platform_SaveEdit', // 另存新作品并编辑
  QUEUE_ACCELERATE = 'youyan_platform_PayQueue', // 排队加速
  CLICK_PLAY_VIDEO = 'youyan_platform_play', // 播放视频
  CLICK_SOURCE_TAG = 'youyan_platform_SourceClick', // 点击来源
  CLICK_CREATE_TEMPLATE_FROM_CHENGPIAN = 'youyan_platform_CreateTemplate', // 创建成片模板
  // YOUYAN_PLATFORM_USETEMPLATE = 'youyan_platform_UseTemplate', // 使用成片模板 todo: 删掉
  CLICK_PUBLISH_CHENGPIAN = 'youyan_platform_publish', // 发布
  CLICK_PUBLISH_TO_YOUYAN_PLATFORM = 'youyan_platform_publish_to_platform', // 发布至平台
  CLICK_PUBLISH_TO_YOUYAN_PLATFORM_APPLY = 'youyan_platform_publish_to_platform_apply', // 提交发布至平台申请
  CLICK_PUT_ON_CHENGPIAN = 'youyan_platform_put_on_chengpian', // 上架成片
  CLICK_PUT_OFF_CHENGPIAN = 'youyan_platform_put_off_chengpian', // 下架成片
  CLICK_WITHDRAW_CHENGPIAN = 'youyan_platform_withdraw_chengpian', // 撤回成片发布申请
  CLICK_DELETE_PERSONAL_PAGE_CHENGPIAN = 'youyan_platform_delete_personal_page_chengpian', // 删除个人/团队主页成片
  CLICK_VIEW_VERIFY_REJECT_REASON = 'youyan_platform_view_verify_reject_reason', // 查看成片发布审核失败原因
  CLICK_COPY_PERSONAL_PAGE_CHENGPIAN = 'youyan_platform_copy_personal_page_chengpian', // 复制个人/团队主页成片
  PAGE_PERSONAL_PAGE_VIEW = 'youyan_platform_personal_page_view', // 查看个人/团队主页

  YOUYAN_PLATFORM_UPLOAD = 'youyan_platform_upload', // 上传
  CLICK_DOWNLOAD_VIDEO = 'youyan_platform_download', // 下载
  YOUYAN_PLATFORM_COPYTOTEAM = 'youyan_platform_CopyToTeam', // 复制到团队空间
  YOUYAN_PLATFORM_COPYTOMY = 'youyan_platform_CopyToMy', // 复制到个人空间
  RENAME_TARGET = 'youyan_platform_rename', // 重命名
  DELETE_TARGET = 'youyan_platform_delete', // 删除
  SET_AS_BRAND = 'youyan_platform_SetAsBrand', // 设为品牌规范
  CANCEL_BRAND_KIT = 'youyan_platform_CancelBrand', // 取消品牌规范
  REMOVE_OPUS_FROM_RECENTLY = 'youyan_platform_remove_from_recently', // 从最近作品中移除
  RESTORE_RECYCLE_BIN = 'youyan_platform_restore', // 回收站-恢复
  DELETE_TARGET_COMPLETE = 'youyan_platform_DeleteComplete', // 回收站-彻底删除
  FOLLOW_BRAND = 'youyan_platform_BrandFollow', // 品牌跟随
  JOIN_TEAM = 'youyan_platform_JoinTeam', // 加入团队
  KICK_FROM_TEAM = 'youyan_platform_kick_someone_from_team', // 将成员提出团队
  CLICK_RESTORE_TO_TEAM = 'youyan_platform_restore_someone_to_team', // 将成员恢复到团队
  CLICK_MEMBER_USE_DETAIL = 'youyan_platform_member_use_detail', // 团队成员使用明细
  TRANSFER_ADMINISTRATOR_SUCCESS = 'youyan_platform_transfer_administrator_success', // 转让管理员成功

  PERSONAL_PACKAGE_SUBSCRIBE = 'youyan_platform_subscribe_personal_package', // 个人套餐订阅
  TEAM_PACKAGE_SUBSCRIBE = 'youyan_platform_subscribe_team_package', // 团队套餐订阅
  PERSONAL_PACKAGE_PAYED = 'youyan_platform_payment_personal_package', // 个人套餐购买
  TEAM_PACKAGE_PAYED = 'youyan_platform_payment_team_package', // 团队套餐购买
  DEDICATED_LINE_PAYED = 'youyan_platform_payment_dedicated_line', // 专线购买
  SUBLICENSE_PAYED = 'youyan_platform_payment_sublicense', // 转授权加购
  CREDIT_PAYED = 'youyan_platform_payment_credit', // 言币加购
  STORAGE_PAYED = 'youyan_platform_payment_storage', // 加购存储空间

  COPY_TEAM_MEMBER_INVITE_LINK = 'youyan_platform_copy_team_member_invite_link', // 获取团队成员邀请链接

  YOUYAN_USER_PROPERTY = 'youyan_platform_user_property', // 用户属性
  YOUYAN_TEAM_PROPERTY = 'youyan_platform_team_property', // 团队属性
  YOUYAN_LOGIN_CLICK_LOGO = 'youyan_platform_login_ClickLogo', // 登录页左侧logo
  YOUYAN_LOGIN_CLICK_REGISTER = 'youyan_platform_login_ClickRegister', // 登录页立即注册
  CLICK_TEMPLATE_MORE = 'youyan_platform_template_more', // 查看更多模板
  CLICK_TUTORIAL_PLAY = 'youyan_platform_tutorial_play', // 教程点击查看
  YOUYAN_PLATFORM_REDEMPTIONCODE_ENTRY = 'youyan_platform_RedemptionCode_Entry', // 点击兑换码兑换入口
  YOUYAN_PLATFORM_EXCHANGECODE_SUBMIT = 'youyan_platform_ExchangeCode_submit', // 提交兑换码
  YOUYAN_PLATFORM_EXCHANGECODE_PACKAGE = 'youyan_platform_ExchangeCode_Package', // 成功兑换套餐权益
  YOUYAN_PLATFORM_EXCHANGECODE_SUBMIT_TOKEN = 'youyan_platform_ExchangeCode_submit_Token', // 成功兑换言币权益
  LOG_OFF_ACCOUNT = 'youyan_platform_log_off_account', // 注销按钮点击弹窗显示
  LOG_OFF_ACCOUNT_VERIFY = 'youyan_platform_log_off_account_verification', // 注销身份验证
  LOG_OFF_ACCOUNT_SUCCESS = 'youyan_platform_log_off_account_success', // 注销成功
  FEEDBACK_CLICK = 'youyan_platform_click_feedback', // 反馈途径
  FEEDBACK_CONFIRM = 'youyan_platform_feedbackconfirm', // 确认提交反馈

  // 私域引流
  USER_REGISTER_SUCCESS = 'youyan_platform_user_register_success', // 用户注册成功
  TEAM_REGISTER_SUCCESS = 'youyan_platform_team_register_success', // 团队体验注册成功
  USER_EXCHANGE_CODE_SUCCESS = 'youyan_platform_user_exchange_code_success', // 用户兑换码成功
  USER_PAYMENT_SUCCESS = 'youyan_platform_user_payment_success', // 个人用户支付成功
  TEAM_PAYMENT_SUCCESS = 'youyan_platform_team_payment_success', // 团队入门支付成功

  // 网站搜索埋点
  CLICK_SITE_SEARCH = 'youyan_platform_research', // 网站搜索
  CLICK_HOT_KEYWORD_SEARCH = 'youyan_platform_research_keywords', // 点击热门搜索关键词
  CLICK_SITE_SEARCH_RESULT_LOAD = 'youyan_platform_research_result_load', // 网站搜索结果加载
  CLICK_SITE_SEARCH_RESULT_CLICK = 'youyan_platfrom_research_result_click', // 网站搜索结果点击
  CLICK_HELP_CENTER_FEEDBACK = 'youyan_platform_helpcenter_feedback', // 帮助中心反馈
  CLICK_HELP_CENTER_DOC_PAGE_EXIT = 'youyan_platform_helpcenter_docpage_exit', // 帮助中心文档页退出

  // 数字人埋点
  PAGE_CHARACTER_DETAIL = 'youyan_platform_character_detail', // 数字人详情页
  CLICK_CHARACTER_USE = 'youyan_platform_3davatar_use', // 数字人用ta出镜
  CLICK_CHARACTER_EDIT = 'youyan_platform_3davatar_edit', // 数字人改造形象

  YOUYAN_PLATFORM_SHARE_SCENE = 'youyan_platform_share_scene', // 场景分享
}

export enum TARGET_FROM {
  Project = 'Project', // 作品；
  EV = 'EV', // 成片；
  Template = 'Template', // 成片模板；
  Studio = 'Studio', // 场景；
  VirtualHuman = 'VirtualHuman', // 人物；
  Timbre = 'Timbre', // 音色；
  SubtitleTemplate = 'SubtitleTemplate', // 字幕模板；
  TextTemplate = 'TextTemplate', // 文字模板；
  Material = 'Material', // 素材；
  Font = 'Font', // 字体；
  BGM = 'BGM', // 音乐；
  Audio = 'Audio', // 音效；
  Sticker = 'Sticker', // 贴纸；
  Title = 'Title', // 片头；
  Credit = 'Credit', // 片尾
}

// 来源 (埋点相关)
export enum SOURCE {
  HOME = 'Home',
  NEWS = 'News', // 产品动态页
  SCHOOL = 'School', // 魔珐学院
  SEARCH_RESULT = 'SearchResult', // 搜索
  RECENTLY = 'Recently', // 最近作品
  MYSPACE = 'MySpace', // 个人空间
  COLLECT = 'Collect', // 我的收藏
  TEAM_SPACE = 'TeamSpace', // 团队空间
  SHARE_WITH_ME = 'ShareWithMe', // 与我分享
  HEADER = 'Header', // 顶部导航
  BRAND = 'Brand', // 品牌规范
  Tutorial = 'Tutorial', // 活动
  PLATFORM = 'Platform', // 服务平台
  PERSONAL_PAGE = 'PersonalPage', // 个人主页
  SUBSCRIBE = 'Subscribe', // 付费订阅
  SCENARIOS = 'Scenarios', // 应用场景
  INDUSTRY = 'Industry', // 行业集合
  INDUSTRY_DETAIL = 'IndustryDetail', // 行业详情
  CASE = 'Case',
  TEMPLATE_CENTER = 'TemplateCenter', // 作品中心
  EDITOR = 'Editor', // 编辑器
  PAY_PAGE = 1, // 付费订阅页面
  RIGHT_CONTINUE = 2, // 权益管理页面-续费
  RIGHT_MANAGE = 3, // 权益管理页面-升级
  HELP_CENTER = 'HelpCenter', // 帮助中心
  SITE_SEARCH = 'SiteSearch', // 全站搜索
  CHARACTER = 'Character', // 数字人物
  CHARACTER_DETAIL = 'CharacterDetail', // 数字人物
  CHARACTER_DETAIL_COMMON_RACE = 'Character_Common_Race',
  CHARACTER_DETAIL_COMMON_SCENARIOS = 'Character_Common_Scenarios',
}

// 模板来源
export enum TEMPLATE_SOURCE {
  HOMEPAGE = 'homepage', // 首页
  TEMPLATE_PAGE = 'template_page', // 模板中心
  SCENARIO_PAGE = 'scenario_page', // 应用场景
  BEST_PRACTICE_PAGE = 'best_practice_page', // 用户优秀作品
  SCENARIO_PAGE_DOWNPART = 'scenario_page_downpart', // 应用场景底部更多
  SIMILAR_RECOM = 'similar_recom', // 相似推荐
  HOME_CREATE = 'home_create', // 首页创作同款
  CENTER_CREATE = 'center_create', // 模板中心创作同款
  MODAL_CREATE = 'modal_create', // 新建作品使用模板
  INDUSTRY_SIMILAR = 'industry_similar', // 更多行业案例
  EDITOR_CREATE = 'editor_create', // 编辑器触发创建
  HIGHLIGHTS = 'highlights', // 首页产品亮点
  PERSONAL_PAGE = 'personal_page', // 个人/团队主页
  CHARACTER = 'Character', // 数字人物
  SEARCH = 'Search', // 搜索
}

// 分享
export enum SHARETO {
  INTERNAL = 1, // 内部
  EXTERNAL = 2, // 外部
}

// 来源
export enum CLASS {
  HOME = 1, // 首页
  LEARN = 2, // 教程页面
}

// 案例和行业来源
export enum PUBLICIZE_FORM {
  HOME = 1, // 首页
  OTHER = 2, // 详情页
}

// 打开入口
export enum OPEN_ENTRANCE {
  CARD = 1, // 点击卡片
  MORE = 2, // 右上角更多中打开
  MODEL = 3, // 弹窗中打开
}

// 排队加速后 是否取消
export enum PAYQUEUECANCEL {
  CANCEL = 1, // 取消
  UNCANCEL = 2, // 未取消
}

// 是否排队加速完成并成功输出成片
export enum PAYQUEUESUC {
  SUCCESS = 1, // 成功
  FAILED = 2, // 失败
}

// 是否批量
export enum BATCH {
  ALL = 1, // 批量
  ONE = 0, // 不批量
}

// 新建作品
export enum CREATE_OPUS {
  DEFAULT = 1, // 默认
  HORIZONTAL, // 横屏
  VERTICAL, // 竖屏
}

// 设为品牌规范
export enum SETASBRAND {
  TEAM = 1, // 从团队空间中设立
  BRAND = 2, // 从品牌规范中选择设立
  LOCAL = 3, // 从品牌规范中点击从本地上传
}

// 是否新用户
export enum USER_TYPE {
  OLD = 2,
  NEW = 1,
}

// 登录方式
export enum LOGIN_WAY {
  PHONE = 1, // 手机验证
  PASSWORD = 2, // 账号密码
  EMAIL = 3, // 邮箱密码
  GOOGLE = 4, // google
  WECHAT = 5, // 微信
}

// 验证结果
export enum VERIFY_RESULT {
  SUCCESS = 1,
  FAIL = 2,
}

// 活动来源
export enum ACTIVITY_SOURCE {
  OTHER = 0, // 其他来源
  INVITE = 1, // 拉新活动
}

// 反馈 type
export enum FEEDBACK_TYPE {
  ERROR = 1,
  SUGGESTION = 2,
  OTHERS = 3,
}

// 对应target的key值
export const TAB_TRACK_KEY: any = {
  [COLLECTION_MAP.OPUS]: TARGET_FROM.Project, // 作品
  [COLLECTION_MAP.PIECES]: TARGET_FROM.EV, // 成片
  [COLLECTION_MAP.PIECES_TEMPLATE]: TARGET_FROM.Template, // 成片模板
  [COLLECTION_MAP.SOURCE]: {
    // 资源
    [RESOURCE_TYPE.STUDIO]: TARGET_FROM.Studio,
    [RESOURCE_TYPE.PERSON]: TARGET_FROM.VirtualHuman,
    [RESOURCE_TYPE.TIMBRE]: TARGET_FROM.Timbre,
    [RESOURCE_TYPE.SUBTITLE_TEMPLATE]: TARGET_FROM.SubtitleTemplate,
    [RESOURCE_TYPE.MATERIAL]: TARGET_FROM.Material,
    [RESOURCE_TYPE.MIX_TEXT_TEMPLATE]: TARGET_FROM.TextTemplate,
    [RESOURCE_TYPE.FONT]: TARGET_FROM.Font,
    [RESOURCE_TYPE.MUSIC]: TARGET_FROM.BGM,
    [RESOURCE_TYPE.SOUND_EFFECT]: TARGET_FROM.Audio,
    [RESOURCE_TYPE.PASTER]: TARGET_FROM.Sticker,
    [RESOURCE_TYPE.HEAD]: TARGET_FROM.Title,
    [RESOURCE_TYPE.END]: TARGET_FROM.Credit,
  },
};

const idPrefixMap = {
  CN: '',
  US: 'en',
  HK: 'hk',
};

export const yfxLogin = (user: UserInfo) => {
  if (import.meta.server) {
    return;
  }
  // 页面打开触发-一般用于打开新页面采集数据
  window._yfx_trackdata = window._yfx_trackdata || [];
  const user_arr = { userid: user.user_id, username: user.account_name };
  window._yfx_trackdata.push(['userset', user_arr]);

  window._dgt = window._dgt || [];
  window._dgt.push(['track_userid', user.user_id]);
  window._dgt.push(['trackAttr', ['username'], [user.account_name]]);
};

export const sensorsLogin = (id: number) => {
  if (import.meta.server) {
    return;
  }

  const prefix = idPrefixMap[getArea().toUpperCase() as Area];
  const loginId = prefix ? `yyuser_${prefix}_${id}` : `yyuser_${id}`;
  window.$sensors?.login(loginId);
};

export const sensorsSetProfile = (user: UserInfo) => {
  if (import.meta.server) {
    return;
  }

  const source = sessionGetItem(SESSION_SOURCE) || undefined;

  const params: Record<string, string | number> = {
    user_type: user.rights_name,
    ma_info_id: user.ma_info_id,
  };

  if (source) {
    params.source = source;
  }

  window.$sensors?.setProfile(params);
};

export const sensorsRegisterPage = (loginInfo?: LoginInfo) => {
  if (import.meta.server) {
    return;
  }

  const params: any = {
    referer: document.referrer,
    version: getEnv('release'),
    area: (window.__NUXT__ as any).config.public.area,
    env: (window.__NUXT__ as any).config.public.env,
    product_line: 'youyan',
    pageReferer_string() {
      if (import.meta.client) {
        try {
          const historyStore = useRouteHistory();
          if (historyStore.value.length) {
            return historyStore.value[historyStore.value.length - 1].fullPath;
          }
        } catch (e) {
          console.error(e);
        }
      }
      return '';
    },
  };

  if (loginInfo) {
    if ('is_new_user' in loginInfo) {
      params.NewUser = loginInfo.is_new_user ? USER_TYPE.NEW : USER_TYPE.OLD;
    } else {
      params.NewUser = dayjs().isSame(dayjs(loginInfo.create_time!), 'day')
        ? USER_TYPE.NEW
        : USER_TYPE.OLD;
    }
  }

  window.$sensors?.registerPage(params);
};

export const sensorsLogout = () => {
  if (import.meta.server) {
    return;
  }
  window.$sensors?.logout?.();
};

interface ResourceLocation {
  source: SOURCE;
}

/**
 * 给埋点使用，有的埋点需要埋入当前从那个模块埋的
 */
export const provideResourceLocation = (params: Reactive<ResourceLocation> | ResourceLocation) => {
  provide('resourceLocation', params);
};

export const injectResourceLocation = () => {
  return inject<Reactive<ResourceLocation> | ResourceLocation>('resourceLocation');
};

export const trackEvent = (event_name: TRACK_EVENT, event_params?: any) => {
  if (import.meta.server) {
    return;
  }

  const { isMobile } = useDevice();
  const track = window.$sensors?.track;

  if (!track) {
    return;
  }

  track(event_name, {
    platform: isMobile ? 'mobile' : 'pc',
    ...event_params,
  });
};

export const trackYfxEvent = (cate: string, name: string, attribute?: Record<string, any>) => {
  if (import.meta.server) {
    return;
  }
  window._yfx_trackdata = window._yfx_trackdata || [];
  window._yfx_trackdata.push(['event', cate, name, attribute]);
};
